import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(identifier.trim(), password);
      toast.success(`Welcome, ${user.username}`);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left banner */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-slate-100 p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1523292562811-8fa7962a78c8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg bg-blue-800 flex items-center justify-center">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            <p className="font-heading font-extrabold text-lg leading-tight">Haryana Safai Mahakumbh</p>
            <p className="text-xs text-slate-400 tracking-wide uppercase">Civic Event Registration</p>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-tight">
            Urban Local Body Registration & Administration
          </h1>
          <p className="mt-4 text-slate-300 text-base leading-relaxed">
            Manage citizen registrations across districts and municipalities with a
            single, authoritative command centre.
          </p>
        </div>
        <p className="relative z-10 text-xs text-slate-500">
          Government of Haryana · Urban Local Bodies Department
        </p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="h-10 w-10 rounded-lg bg-blue-900 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="font-heading font-bold text-lg text-slate-900">Haryana Safai Mahakumbh</span>
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Use your username or email to continue.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="identifier">Username or Email</Label>
              <Input
                id="identifier"
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="admin or you@example.com"
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full bg-blue-900 hover:bg-blue-800"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
